import React, { useState, useEffect } from 'react';
import { api, formatTime, getRoleLabel } from '../api';

export default function Dashboard({ user, setActivePage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard').then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="loading-center">
      <div className="loading-spinner" />
    </div>
  );

  return (
    <div className="page fade-in">
      {/* Greeting */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2 }}>
          {getRoleLabel(user.role)} · {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <h1 className="page-title">Halo, {user.name.split(' ')[0]} 👋</h1>
      </div>

      {/* Admin & SPV Dashboard */}
      {(user.role === 'admin' || user.role === 'spv') && data && (
        <>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-value">{data.hadir_hari_ini || 0}</div>
              <div className="stat-label">Hadir Hari Ini</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-value">{data.laporan_hari_ini || 0}</div>
              <div className="stat-label">Laporan Hari Ini</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔔</div>
              <div className="stat-value" style={{ color: data.request_pending > 0 ? 'var(--red)' : 'var(--gold-400)' }}>
                {data.request_pending || 0}
              </div>
              <div className="stat-label">Request Pending</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏢</div>
              <div className="stat-value">{data.total_anggota || 0}</div>
              <div className="stat-label">{user.role === 'admin' ? 'Total Anggota' : 'Anggota Wilayah'}</div>
            </div>
          </div>

          {user.role === 'admin' && (
            <div className="stat-card" style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div className="stat-icon">🗺️</div>
                  <div className="stat-value">{data.total_wilayah || 0}</div>
                  <div className="stat-label">Total Wilayah Aktif</div>
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setActivePage('admin', 'wilayah')}>
                  Kelola →
                </button>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="section">
            <div className="section-header">
              <span className="section-title">Aksi Cepat</span>
            </div>
            <div className="grid-2">
              <button className="btn btn-outline btn-full" onClick={() => setActivePage('laporan')} style={{ padding: 16, flexDirection: 'column', gap: 8, height: 'auto' }}>
                <span style={{ fontSize: '1.5rem' }}>📋</span>
                <span>Lihat Laporan</span>
              </button>
              <button className="btn btn-outline btn-full" onClick={() => setActivePage('request')} style={{ padding: 16, flexDirection: 'column', gap: 8, height: 'auto' }}>
                <span style={{ fontSize: '1.5rem' }}>🔔</span>
                <span>Request Client</span>
              </button>
              <button className="btn btn-outline btn-full" onClick={() => setActivePage('jadwal')} style={{ padding: 16, flexDirection: 'column', gap: 8, height: 'auto' }}>
                <span style={{ fontSize: '1.5rem' }}>📅</span>
                <span>Jadwal</span>
              </button>
              {user.role === 'admin' && (
                <button className="btn btn-gold btn-full" onClick={() => setActivePage('admin')} style={{ padding: 16, flexDirection: 'column', gap: 8, height: 'auto' }}>
                  <span style={{ fontSize: '1.5rem' }}>⚙️</span>
                  <span>Kelola Sistem</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Anggota Dashboard */}
      {user.role === 'anggota' && data && (
        <>
          {/* Status Absen Hari Ini */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="section-title" style={{ marginBottom: 12 }}>Status Absen Hari Ini</div>
            {data.absen_hari_ini?.length > 0 ? (
              <div>
                {data.absen_hari_ini.map((a, i) => (
                  <div key={i} className="list-item" style={{ cursor: 'default' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: a.type === 'masuk' ? 'var(--green-soft)' : 'var(--red-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                      {a.type === 'masuk' ? '✅' : a.type === 'keluar' ? '🚪' : '📝'}
                    </div>
                    <div className="list-item-content">
                      <div className="list-item-title" style={{ textTransform: 'capitalize' }}>Absen {a.type}</div>
                      <div className="list-item-sub">{formatTime(a.waktu)}</div>
                    </div>
                    <span className={`badge ${a.type === 'masuk' ? 'badge-green' : 'badge-red'}`}>
                      {a.type}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>⏰</div>
                <p style={{ fontSize: '0.85rem' }}>Belum ada absen hari ini</p>
              </div>
            )}
            <button className="btn btn-gold btn-full" style={{ marginTop: 12 }} onClick={() => setActivePage('absen')}>
              Absen Sekarang
            </button>
          </div>

          {/* Jadwal Minggu Ini */}
          <div className="section">
            <div className="section-header">
              <span className="section-title">Jadwal 7 Hari ke Depan</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setActivePage('jadwal')}>Lihat Semua</button>
            </div>
            {data.jadwal_minggu?.length > 0 ? (
              data.jadwal_minggu.map((j, i) => (
                <div key={i} className="list-item" style={{ cursor: 'default' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--gold-glow)', border: '1px solid var(--border-hover)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '0.65rem', color: 'var(--gold-400)', fontWeight: 600 }}>
                      {new Date(j.tanggal).toLocaleDateString('id-ID', { month: 'short' }).toUpperCase()}
                    </span>
                    <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--gold-400)', lineHeight: 1 }}>
                      {new Date(j.tanggal).getDate()}
                    </span>
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-title" style={{ textTransform: 'capitalize' }}>Shift {j.shift}</div>
                    <div className="list-item-sub">{new Date(j.tanggal).toLocaleDateString('id-ID', { weekday: 'long' })}</div>
                  </div>
                  <span className={`badge ${j.status === 'aktif' ? 'badge-green' : j.status === 'libur' ? 'badge-gray' : 'badge-yellow'}`}>
                    {j.status}
                  </span>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📅</div>
                <p>Belum ada jadwal minggu ini</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Client Dashboard */}
      {user.role === 'client' && data && (
        <>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-value">{data.total_request || 0}</div>
              <div className="stat-label">Total Request</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏳</div>
              <div className="stat-value" style={{ color: data.request_pending > 0 ? 'var(--yellow)' : 'var(--gold-400)' }}>
                {data.request_pending || 0}
              </div>
              <div className="stat-label">Menunggu</div>
            </div>
          </div>

          {/* Anggota yang jaga hari ini */}
          <div className="section">
            <div className="section-header">
              <span className="section-title">Petugas Jaga Hari Ini</span>
            </div>
            {data.anggota_jaga_hari_ini?.length > 0 ? (
              data.anggota_jaga_hari_ini.map((a, i) => (
                <div key={i} className="list-item" style={{ cursor: 'default' }}>
                  <div className="list-item-avatar">
                    {a.foto_url ? <img src={a.foto_url} alt="" /> : a.name?.charAt(0)}
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-title">{a.name}</div>
                    <div className="list-item-sub">Sedang bertugas</div>
                  </div>
                  <span className="badge badge-green">● Aktif</span>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">👮</div>
                <p>Belum ada petugas absen masuk hari ini</p>
              </div>
            )}
          </div>

          <button className="btn btn-gold btn-full btn-lg" onClick={() => setActivePage('request')}>
            + Buat Request Baru
          </button>
        </>
      )}
    </div>
  );
}
